import { cacheExchange, Client, createClient, fetchExchange } from '@urql/core';
import { persistedExchange } from '@urql/exchange-persisted';

import getConfigByBrand from '@src/utils/get-config-by-brand';
import { contentfulGraphQLURL } from 'codegen';

const brandConfig = getConfigByBrand((process.env.TAG_BRAND_THEME || 'lovet') as TagThemeType);

const urqlPersistedExchange = persistedExchange({
	preferGetForPersistedQueries: true,
	enforcePersistedQueries: false,
});

const urqlExchanges = [cacheExchange, urqlPersistedExchange, fetchExchange];

const graphQlClient = createClient({
	url: contentfulGraphQLURL,
	exchanges: urqlExchanges,
	requestPolicy: 'network-only',
	fetchOptions: () => {
		return {
			headers: {
				Authorization: `Bearer ${process.env.CONTENTFUL_ACCESS_TOKEN}`,
				'content-type': 'application/json',
			},
		};
	},
});

const previewGraphQlClient = createClient({
	url: contentfulGraphQLURL,
	exchanges: urqlExchanges,
	requestPolicy: 'network-only',
	fetchOptions: () => {
		return {
			headers: {
				Authorization: `Bearer ${process.env.CONTENTFUL_PREVIEW_ACCESS_TOKEN}`,
				'content-type': 'application/json',
			},
		};
	},
});

export const client = graphQlClient;
export const previewClient = previewGraphQlClient;

export const asyncClient = () => {
	const accessToken = process.env.TAG_CONTENTFUL_ACCESS_TOKEN;

	if (!accessToken) {
		return null;
	}
	const graphQlClient = createClient({
		url: contentfulGraphQLURL,
		exchanges: urqlExchanges,
		requestPolicy: 'network-only',
		fetchOptions: () => {
			return {
				headers: {
					Authorization: `Bearer ${accessToken}`,
					'content-type': 'application/json',
				},
			};
		},
	});
	return graphQlClient;
};

export const asyncPreviewClient = () => {
	const previewAccessToken = process.env.TAG_CONTENTFUL_PREVIEW_ACCESS_TOKEN;
	if (!previewAccessToken) {
		return null;
	}
	const previewGraphQlClient = createClient({
		url: contentfulGraphQLURL,
		exchanges: urqlExchanges,
		requestPolicy: 'network-only',
		fetchOptions: () => {
			return {
				headers: {
					Authorization: `Bearer ${previewAccessToken}`,
					'content-type': 'application/json',
				},
			};
		},
	});

	return previewGraphQlClient;
};

let urqlClient: Client | null = null;
let urqlPreviewClient: Client | null = null;

export const getDefaultContentClient = () => {
	urqlClient ??= asyncClient();
	return urqlClient;
};

export const getPreviewContentClient = () => {
	urqlPreviewClient ??= asyncPreviewClient();
	return urqlPreviewClient;
};

let vetspireClient: Client | null = null;

export const getVetspireClient = () => {
	if (vetspireClient === null) {
		const apiKey = process.env.TAG_VETSPIRE_API_KEY;
		vetspireClient = new Client({
			url:
				(process.env.ENVIRONMENT_NAME === 'prod'
					? brandConfig.features.vetspire?.prod
					: brandConfig.features.vetspire?.nonprod) ?? '',
			exchanges: [cacheExchange, fetchExchange],
			requestPolicy: 'network-only',
			fetchOptions: () => {
				return {
					headers: {
						Authorization: apiKey ?? '',
					},
				};
			},
		});
	}
	return vetspireClient;
};

const beffeGraphQlClientsByUrl = new Map<string, Client>();

export const getBeffeGraphQlClient = (url: string) => {
	const existingClient = beffeGraphQlClientsByUrl.get(url);
	if (existingClient) {
		return existingClient;
	}

	const client = createClient({
		url,
		exchanges: [cacheExchange, fetchExchange],
		requestPolicy: 'network-only',
		fetchOptions: () => {
			return {
				headers: {
					'content-type': 'application/json',
				},
			};
		},
	});

	beffeGraphQlClientsByUrl.set(url, client);
	return client;
};
